import { z } from 'zod'
import { TIPO_NOTIFICACAO, TIPO_TAREFA, type TipoNotificacao, type TipoTarefa } from '../enums'
import { booleanoDeQuery, paginacaoSchema, textoObrigatorio } from './comum'

/**
 * Fase 4 — operação: notificações (5.6), tarefas e calendário (5.3),
 * respostas rápidas (5.1) e mensagem agendada (5.1 e seção 8).
 */

// ─────────────────────────── notificações ───────────────────────────

export const ROTULO_TIPO_NOTIFICACAO: Record<TipoNotificacao, string> = {
  tarefa: 'Tarefa',
  cronometro: 'Cronômetro estourado',
  sistema: 'Sistema',
  sem_resposta: 'Sem resposta',
  fila_parada: 'Parado na fila',
  mensagem_para_fora: 'Mensagem para quem está fora',
  agendada_respondida: 'Cliente respondeu antes do agendado',
  retorno_marcado: 'Retorno marcado',
  marketing: 'Marketing',
}

export const TIPOS_NOTIFICACAO = TIPO_NOTIFICACAO

/** 1 é o mais urgente. A escala é fechada de propósito: quatro degraus, quatro cores. */
export type PrioridadeNotificacao = 1 | 2 | 3 | 4

/**
 * A prioridade de cada tipo de aviso.
 *
 * **A cor na tela sai daqui, nunca do tipo.** Dois avisos de mesma prioridade
 * aparecem com a mesma cor, ainda que sejam de tipos diferentes — é isso que
 * faz a cor significar "o quanto isto corre" em vez de "que assunto é este".
 * Sem essa regra, quem usa precisa decorar seis cores; com ela, decora quatro
 * degraus e lê qualquer aviso novo que apareça depois.
 *
 * Os degraus:
 *
 * 1. o cliente está esperando e alguém precisa agir agora;
 * 2. existe trabalho sem dono — ninguém está agindo, e ninguém errou ainda;
 * 3. trabalho previsto, com hora marcada;
 * 4. só informa que algo terminou; não pede nada de ninguém.
 */
export const PRIORIDADE_NOTIFICACAO: Record<TipoNotificacao, PrioridadeNotificacao> = {
  cronometro: 1,
  sem_resposta: 1,
  fila_parada: 2,
  tarefa: 3,
  sistema: 4,
  // O cliente escreveu e quem atende não está: alguém precisa agir agora.
  mensagem_para_fora: 1,
  // Pede uma decisão de quem agendou: manter a mensagem ou cancelar.
  agendada_respondida: 3,
  // Só informa que existe um retorno à frente; o aviso de quando ele chega é a tarefa.
  retorno_marcado: 4,
  // Hora de publicar e post para aprovar: trabalho previsto, com hora marcada.
  marketing: 3,
}

export const ROTULO_PRIORIDADE: Record<PrioridadeNotificacao, string> = {
  1: 'Crítica',
  2: 'Alta',
  3: 'Normal',
  4: 'Informativa',
}

export interface RespostaNotificacoes {
  /** Quantas ainda não foram dispensadas — é o número na bolinha do sino. */
  pendentes: number
  /** As pendentes de cada tipo, para o contador de cada marca da borda. */
  pendentesPorTipo: Record<TipoNotificacao, number>
  /** Quantas casam com o filtro, para a paginação da tela. */
  total: number
  itens: NotificacaoDto[]
}

/**
 * As três marcas da borda da tela (ADR 0035), decididas pelo Mike: o "!"
 * vermelho para cliente esperando, o triângulo amarelo para o resto que pede
 * ação, a bolinha verde para o que só informa. Sai da prioridade, e não do
 * tipo: tipo novo cai sozinho na marca certa.
 */
export type GrupoDaNotificacao = 'cliente' | 'acao' | 'informa'
export const GRUPOS_DA_NOTIFICACAO: GrupoDaNotificacao[] = ['cliente', 'acao', 'informa']

export function grupoDaNotificacao(tipo: TipoNotificacao): GrupoDaNotificacao {
  const prioridade = PRIORIDADE_NOTIFICACAO[tipo]
  if (prioridade === 1) return 'cliente'
  if (prioridade === 4) return 'informa'
  return 'acao'
}

export const ROTULO_GRUPO_DA_NOTIFICACAO: Record<GrupoDaNotificacao, string> = {
  cliente: 'Cliente esperando',
  acao: 'Pede ação',
  informa: 'Só informa',
}

export function tiposDoGrupo(grupo: GrupoDaNotificacao): TipoNotificacao[] {
  return TIPO_NOTIFICACAO.filter((t) => grupoDaNotificacao(t) === grupo)
}

export interface NotificacaoDto {
  id: string
  tipo: TipoNotificacao
  titulo: string
  corpo: string | null
  entidade: string | null
  entidadeId: string | null
  lidaEm: string | null
  dispensadaEm: string | null
  criadoEm: string
}

/**
 * O sino pede a primeira página das pendentes; a tela de notificações usa o
 * mesmo endpoint com paginação e recorte por tipo.
 */
export const filtroNotificacoesSchema = paginacaoSchema.extend({
  incluirDispensadas: booleanoDeQuery(false),
  tipo: z.enum([...TIPO_NOTIFICACAO, 'todos']).default('todos'),
  /** Os avisos de uma marca da borda. Junta com `tipo` quando os dois vêm. */
  grupo: z.enum(['cliente', 'acao', 'informa']).optional(),
})

// ─────────────────────────── tarefas ───────────────────────────

export const ROTULO_TIPO_TAREFA: Record<TipoTarefa, string> = {
  ligacao: 'Ligação',
  reuniao: 'Reunião',
  mensagem: 'Mensagem',
  outro: 'Outro',
}

export const TIPOS_TAREFA = TIPO_TAREFA

export const criarTarefaSchema = z.object({
  titulo: textoObrigatorio('título', 160),
  tipo: z.enum(TIPO_TAREFA).default('outro'),
  descricao: z.string().trim().max(2000).optional().nullable(),
  contatoId: z.string().uuid().optional().nullable(),
  /** Em branco = para mim. */
  usuarioId: z.string().uuid().optional().nullable(),
  dataHora: z.coerce.date(),
  lembrete: z.boolean().default(true),
  som: z.boolean().default(false),
})

export const atualizarTarefaSchema = criarTarefaSchema.partial().extend({
  concluida: z.boolean().optional(),
})

export const filtroTarefasSchema = z.object({
  de: z.coerce.date().optional(),
  ate: z.coerce.date().optional(),
  /** 5.3: caixa "ver apenas minhas", marcada por padrão. */
  apenasMinhas: booleanoDeQuery(true),
  incluirConcluidas: booleanoDeQuery(false),
  contatoId: z.string().uuid().optional(),
})

export interface TarefaDto {
  id: string
  titulo: string
  tipo: TipoTarefa
  descricao: string | null
  contatoId: string | null
  contatoNome: string | null
  usuarioId: string
  usuarioNome: string
  dataHora: string
  concluidaEm: string | null
  lembrete: boolean
  som: boolean
  atrasada: boolean
}

// ─────────────────────── respostas rápidas ───────────────────────

export const criarRespostaRapidaSchema = z.object({
  atalho: z
    .string()
    .trim()
    .min(1, 'informe o atalho')
    .max(40)
    .regex(/^[a-z0-9_-]+$/i, 'use letras, números, hífen ou underline'),
  conteudo: textoObrigatorio('conteúdo', 4000),
  /** Global só o administrador cria (5.8). */
  global: z.boolean().default(false),
})
export type CriarRespostaRapidaEntrada = z.infer<typeof criarRespostaRapidaSchema>

export const atualizarRespostaRapidaSchema = z.object({
  atalho: z.string().trim().min(1).max(40).optional(),
  conteudo: textoObrigatorio('conteúdo', 4000).optional(),
})

export interface RespostaRapidaDto {
  id: string
  atalho: string
  conteudo: string
  global: boolean
  usuarioId: string | null
}

// ─────────────────────── mensagem agendada ───────────────────────

/**
 * Agendamento (5.1). Permite mandar a mesma mensagem para vários contatos,
 * e nesse caso o disparo sai espaçado (regra 5 e seção 8): o número já foi
 * bloqueado uma vez por mandar várias seguidas para quem não tinha conversa
 * recente.
 */
export const agendarMensagemSchema = z
  .object({
    /**
     * Teto de destinatários por lote.
     *
     * Não havia nenhum: um POST com cem mil ids fazia a rota consultar todos,
     * sortear cem mil horários e inserir cem mil linhas em
     * `mensagens_agendadas` numa requisição só — a api travava e o banco ficava
     * com a fila entupida.
     *
     * Mil é folgado para a operação real e ainda protege o número: disparo em
     * massa é o que derruba conta de WhatsApp (REGRA 5), e um lote maior que
     * isso é engano ou abuso, não campanha.
     */
    contatoIds: z
      .array(z.string().uuid())
      .min(1, 'escolha ao menos um contato')
      .max(1000, 'no máximo 1000 contatos por lote'),
    conexaoId: z.string().uuid(),
    /** Vazio só quando vai um modelo, que traz o próprio texto. */
    conteudo: z.string().trim().max(4000, 'mensagem: no máximo 4000 caracteres').default(''),
    /**
     * Modelo aprovado da API oficial (ADR 0033), no lugar do texto. Num lote,
     * os valores das variáveis são os mesmos para todos.
     */
    modelo: z
      .object({
        modeloId: z.string().uuid(),
        valores: z.record(z.string(), z.string().max(1024)).default({}),
      })
      .optional(),
    agendadoPara: z.coerce.date(),
    /** Ao disparar, o lead passa para este vendedor (5.1). */
    vendedorDestinoId: z.string().uuid().optional().nullable(),
    nomeLote: z.string().trim().max(120).optional(),
  })
  .refine((d) => Boolean(d.modelo) || d.conteudo.length > 0, {
    message: 'mensagem: escreva o texto',
    path: ['conteudo'],
  })
export type AgendarMensagemEntrada = z.infer<typeof agendarMensagemSchema>

export const filtroAgendamentosSchema = paginacaoSchema.extend({
  status: z.enum(['pendente', 'enviada', 'cancelada', 'erro']).optional(),
  /** Sem `ver_base_completa` o servidor limita ao que a pessoa criou. */
  contatoId: z.string().uuid().optional(),
  /** A semana do calendário (08/10/2026): as que saem neste intervalo. */
  de: z.coerce.date().optional(),
  ate: z.coerce.date().optional(),
  /** "Ver apenas minhas" do calendário: só as que a pessoa programou. */
  apenasMinhas: booleanoDeQuery(false),
})

export const editarAgendamentoSchema = z.object({
  conteudo: textoObrigatorio('mensagem', 4000).optional(),
  agendadoPara: z.coerce.date().optional(),
  vendedorDestinoId: z.string().uuid().optional().nullable(),
})

export interface AgendamentoDto {
  id: string
  contatoId: string
  contatoNome: string
  conexaoId: string
  conexaoNome: string
  conteudo: string
  agendadoPara: string
  status: 'pendente' | 'enviada' | 'cancelada' | 'erro'
  erro: string | null
  enviadoEm: string | null
  vendedorDestinoId: string | null
  vendedorDestinoNome: string | null
  criadoPorNome: string
  /** A conversa mais recente do cliente, para o calendário abrir direto nela. */
  chamadoId: string | null
  loteId: string | null
  criadoEm: string
}

/** Um retorno marcado, na semana do calendário (08/10/2026). */
export interface RetornoNoCalendarioDto {
  chamadoId: string
  contatoNome: string
  quando: string
  responsavelNome: string | null
}

export const filtroRetornosSchema = z.object({
  de: z.coerce.date(),
  ate: z.coerce.date(),
  apenasMinhas: booleanoDeQuery(true),
})

export interface ResultadoAgendamento {
  criados: number
  loteId: string | null
  /** Quando o lote é grande, o último sai bem depois do horário pedido. */
  ultimoEnvioPrevisto: string
}
